'use client'

import type { LucideIcon } from 'lucide-react'
import { Fragment, type ReactNode } from 'react'

import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { cn } from '@/lib/utils'

import { UnavailableLineTabTrigger } from './unavailable-line-tab-trigger'

export type PageLineTabItem = {
  value: string
  label: ReactNode
  /** Plain string for unavailable aria — defaults to string label when omitted. */
  ariaLabel?: string
  icon?: LucideIcon
  badge?: ReactNode
  disabled?: boolean
  unavailableTitle?: string
  triggerTitle?: string
}

export type PageLineTabsProps = {
  value: string
  onValueChange: (value: string) => void
  ariaLabel: string
  items: readonly PageLineTabItem[]
  renderTrigger?: (item: PageLineTabItem, defaultTrigger: ReactNode) => ReactNode
  className?: string
}

function itemAriaLabel(item: PageLineTabItem): string {
  if (item.ariaLabel) return item.ariaLabel
  if (typeof item.label === 'string') return item.label
  return item.value
}

function DefaultLineTabTrigger({ item }: { item: PageLineTabItem }) {
  const Icon = item.icon
  const labelText = itemAriaLabel(item)

  if (item.unavailableTitle) {
    return (
      <UnavailableLineTabTrigger
        value={item.value}
        label={labelText}
        unavailableTitle={item.unavailableTitle}
      >
        {Icon ? <Icon className="h-4 w-4" aria-hidden /> : null}
        {item.label}
      </UnavailableLineTabTrigger>
    )
  }

  return (
    <TabsTrigger
      variant="line"
      value={item.value}
      className="gap-2"
      disabled={item.disabled}
      title={item.triggerTitle}
    >
      {Icon ? <Icon className="h-4 w-4" aria-hidden /> : null}
      {item.label}
      {item.badge ?? null}
    </TabsTrigger>
  )
}

/** Class B page section switcher — underline tabs under `PageHeader`. */
export function PageLineTabs({
  value,
  onValueChange,
  ariaLabel,
  items,
  renderTrigger,
  className,
}: PageLineTabsProps) {
  return (
    <Tabs value={value} onValueChange={onValueChange} className={cn('mb-6 gap-0', className)}>
      <TabsList variant="line" aria-label={ariaLabel}>
        {items.map((item) => {
          const defaultTrigger = <DefaultLineTabTrigger item={item} />
          return (
            <Fragment key={item.value}>
              {renderTrigger ? renderTrigger(item, defaultTrigger) : defaultTrigger}
            </Fragment>
          )
        })}
      </TabsList>
    </Tabs>
  )
}
